import './hero.css';
import { useCallback, useEffect, useRef } from 'react';
import type { EmblaCarouselType, EmblaEventType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from 'lib/utils';
import { Book, ThumbsUp } from 'lucide-react';
import brandbookImage from './images/brand-book.png';
import socialMediaImage from './images/social-media.png';
import audioToolImage from './images/audio-tool.png';
import textToolImage from './images/text-tool.png';
import imageToolImage from './images/image-tool.png';
import botBuilderImage from './images/bot-builder.png';
import { getDevToolRoute } from 'lib/footer.util';

const carouselItems = [
  {
    title: 'Brandbook',
    icon: Book,
    mainImage: brandbookImage,
    link: getDevToolRoute('BRANDBOOK'),
  },
  {
    title: 'Social Media',
    icon: ThumbsUp,
    mainImage: socialMediaImage,
    link: getDevToolRoute('SOCIAL_MEDIA_GENERATOR'),
  },
  {
    title: 'Audio Tools',
    icon: ThumbsUp,
    mainImage: audioToolImage,
    link: getDevToolRoute('PODCAST_GENERATOR'),
  },
  {
    title: 'Text Tools',
    icon: ThumbsUp,
    mainImage: textToolImage,
    link: getDevToolRoute('BLOG_WRITER'),
  },
  {
    title: 'Image Tools',
    icon: ThumbsUp,
    mainImage: imageToolImage,
    link: getDevToolRoute('TEXT_TO_IMAGE'),
  },
  {
    title: 'Bot Builder',
    icon: ThumbsUp,
    mainImage: botBuilderImage,
    link: getDevToolRoute('BOT_BUILDER'),
  },
];

const HeroCarouselItem = ({
  item,
}: {
  item: (typeof carouselItems)[number];
}) => {
  return (
    <div
      onClick={() => {
        window.open(item.link, '_blank');
      }}
      className={cn(
        'flex flex-col items-center justify-center border-2 border-stroke rounded-2xl bg-card gap-4 cursor-pointer'
      )}
    >
      <div className="flex items-center justify-center bg-primary/10 rounded-2xl">
        <img
          alt={item.title}
          className="w-full h-full rounded-2xl rounded-b-none"
          src={item.mainImage}
        />
      </div>
      <div className="text-center flex items-center gap-2 p-5 px-7 pt-0 w-full">
        <div className="rounded-lg p-4 bg-primary mr-4">
          <item.icon className="size-6 text-text-primary" />
        </div>
        <h3 className="font-medium text-lg text-text-secondary tracking-wider">
          {item.title}
        </h3>
      </div>
    </div>
  );
};

const TWEEN_FACTOR_BASE = 0.25;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

const HeroCarousel = () => {
  const slides = carouselItems;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    dragFree: false,
    containScroll: 'trimSnaps',
    align: 'center',
    slidesToScroll: 1,
  });
  const tweenFactor = useRef(0);
  const tweenNodes = useRef<Array<HTMLElement>>([]);

  const setTweenNodes = useCallback((emblaApi: EmblaCarouselType): void => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
      return slideNode.querySelector('.embla__slide__number') as HTMLElement;
    });
  }, []);

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current =
      TWEEN_FACTOR_BASE * (emblaApi.scrollSnapList().length - 3);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const timeout = setInterval(() => {
      emblaApi?.scrollNext();
    }, 3000);

    return () => {
      clearInterval(timeout);
    };
  }, [emblaApi]);

  const tweenScale = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = eventName === 'scroll';

      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap?.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }

                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          // Invert the scaling logic - larger scale when further from center
          const tweenValue = Math.abs(diffToTarget * tweenFactor.current);

          // Base scale of 1, increasing up to 1.5 based on distance from center
          const scale = numberWithinRange(1 + tweenValue, 1.1, 1.5).toString();
          // const scale = numberWithinRange(1 + tweenValue, 1, 1.2).toString();

          const tweenNode = tweenNodes.current[slideIndex];
          if (tweenNode) {
            tweenNode.style.transform = `scale(${parseFloat(scale) - 0.22})`;
            tweenNode.style.filter = `blur(${tweenValue * 25}px) brightness(${1 - tweenValue * 1.25})`;
          }
        });
      });
    },
    []
  );

  useEffect(() => {
    if (!emblaApi) return;

    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    tweenScale(emblaApi);

    emblaApi
      .on('reInit', setTweenNodes)
      .on('reInit', setTweenFactor)
      .on('reInit', tweenScale)
      .on('scroll', tweenScale)
      .on('slideFocus', tweenScale);
  }, [emblaApi, tweenScale]);

  return (
    <div className="embla_hero max-w-[1280px] mx-auto">
      <div ref={emblaRef}>
        <div className="embla__container_hero">
          {slides.map((item, index) => (
            <div key={index} className="embla__slide px-2">
              <div className="embla__slide__number">
                <HeroCarouselItem item={item} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroCarousel;
